import type { Nullable } from "models-webgl2";
import { CommandAction } from "utils/helpers/classes/command-controller";
import {
  Webgl2Component,
  CropSelectionComponent,
  CanvasResizeSelectionComponent,
  ImageRectComponent,
  ImageAdjustRectComponent,
} from "../../webgl2-component";
import { Texture } from "../../texture";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { Webgl2Tool } from "./webgl2-tool";
import { Vec2 } from "utils/math/vec2";
import { Mat3 } from "utils/math/mat3";
import { CropSelectionBottomComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-bottom-component";
import { CropSelectionBottomLeftComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-bottom-left-component";
import { CropSelectionBottomRightComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-bottom-right-component";
import { CropSelectionLeftComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-left-component";
import { CropSelectionRightComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-right-component";
import { CropSelectionTopComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-top-component";
import { CropSelectionTopLeftComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-top-left-component";
import { CropSelectionTopRightComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-top-right-component";

const DEFAULT_CURSOR = "default";
const CROP_CURSOR_AABB_MARGIN_PX = 40;

export class CropTool extends Webgl2Tool {
  private _currComponent: Nullable<Webgl2Component>;
  private _dispose: Nullable<VoidFunction>;
  private _lastCursor: string = DEFAULT_CURSOR;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();
  }

  public onAssign() {
    const component = this._selectedComponent;
    if (!component) return;

    this._renderer.cropSelectionComponent.copyComponentTransform(component);
    this._initCropSelectionCanvasCentered(component);
    this._setCursor(DEFAULT_CURSOR);
    this._dispose = this._renderer.commands.events.on("history", () => {
      this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
    });
  }

  public onUnAssign() {
    this._handleClearTool();
    this._setCursor(DEFAULT_CURSOR);
  }

  public handleCrop() {
    const component = this._selectedComponent;
    if (!component) return this._handleClearTool();

    const ratio = component.state.texture.textureSize.clone().divide(component.transform.size);
    const undoTexture = component.state.texture.clone();
    const undoTransform = component.transform.clone();
    const prevCanvasSize = this._renderer.canvasSize.clone();
    const prevCanvasTranslation = this._renderer.canvasTranslation.clone();

    const actionTexture = component.state.texture.clone();
    const selectedTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      component,
      Vec2.zero(),
    );
    const cropTopLeftGlobal = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._renderer.cropSelectionComponent,
      Vec2.zero(),
    );
    const cropBottomRightGlobal = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._renderer.cropSelectionComponent,
      Vec2.one(),
    );
    const tm3 = Mat3.identity()
      .rotate(-component.transform.rads)
      .translate(selectedTopLeft.clone().negate());

    const cropTopLeftLocal = cropTopLeftGlobal.transformMat3(tm3.mat);
    const cropBottomRightLocal = cropBottomRightGlobal.transformMat3(tm3.mat);

    const minX = Math.max(0, Math.min(cropTopLeftLocal.x, cropBottomRightLocal.x));
    const maxX = Math.min(
      component.transform.size.width,
      Math.max(cropTopLeftLocal.x, cropBottomRightLocal.x),
    );
    const minY = Math.max(0, Math.min(cropTopLeftLocal.y, cropBottomRightLocal.y));
    const maxY = Math.min(
      component.transform.size.height,
      Math.max(cropTopLeftLocal.y, cropBottomRightLocal.y),
    );

    if (maxX <= minX || maxY <= minY) {
      return this._handleClearTool();
    }

    const cropSize = new Vec2(maxX - minX, maxY - minY).multiply(ratio);
    const cropOffset = new Vec2(minX, minY).multiply(ratio);

    Texture.cropTexture(
      component.state.texture,
      actionTexture,
      cropOffset.x,
      cropOffset.y,
      cropSize.width,
      cropSize.height,
    );
    const targetSize = actionTexture.textureSize.clone();

    this._renderer.commands.execute(
      new CommandAction(
        "Crop Tool",
        () => {
          this._renderer.setCanvasSize(targetSize, true);
          const actionTransform = this._renderer.cropSelectionComponent.transform.clone();
          actionTransform.calcDimensions({
            position: this._renderer.canvasGlobalCenter,
            size: targetSize,
            rotation: component.transform.degrees,
            scale: Vec2.one(),
          });
          this._renderer.selectComponent(component);
          component.transform.copy(actionTransform);
          component.state.texture = actionTexture.clone();
          this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
          this._renderer.selectionComponent.copyComponentTransform(component);
        },
        () => {
          this._renderer.setCanvasSize(prevCanvasSize, false);
          this._renderer.setCanvasTranslate(prevCanvasTranslation, true);
          this._renderer.selectComponent(component);
          component.transform.copy(undoTransform);
          component.state.texture = undoTexture.clone();
          this._renderer.eventsManager.assignTool(this._renderer.tools.cropTool);
          this._renderer.cropSelectionComponent.copyComponentTransform(component);
          this._renderer.selectionComponent.copyComponentTransform(component);
        },
      ),
    );
  }

  public onMouseDown(e: MouseEvent) {
    return this._handleDown(e);
  }

  public onMouseMove(e: MouseEvent) {
    this._handleMove(e);
  }

  public onMouseOver(e: MouseEvent) {
    // While dragging, keep cursor based on current active component.
    if (this._currComponent) {
      this._setCursor(this._getCursorByComponent(this._currComponent));
      return;
    }

    if (!this._selectedComponent) {
      this._setCursor(DEFAULT_CURSOR);
      return;
    }

    // Avoid expensive picking when mouse is clearly outside crop selection area.
    if (!this._isMousePossiblyOverCropSelection(e)) {
      this._setCursor(DEFAULT_CURSOR);
      return;
    }

    const hovered = this._renderer.findComponentUnderMouse(e);
    const root = Webgl2dRenderer.global.getRootParent(hovered).slice(-1)[0];

    if (root instanceof CropSelectionComponent) {
      this._setCursor(this._getCursorByComponent(hovered));
      return;
    }

    this._setCursor(DEFAULT_CURSOR);
  }

  public onMouseLeave() {
    this._setCursor(DEFAULT_CURSOR);
  }

  public onMouseEnded(e: MouseEvent) {
    return this._handleUp(e);
  }

  public onTouchStart(e: TouchEvent) {
    return this._handleDown(e);
  }

  public onTouchMove(e: TouchEvent) {
    this._handleMove(e);
  }

  public onTouchEnded(e: TouchEvent) {
    return this._handleUp(e);
  }

  private _handleDown(e: MouseEvent | TouchEvent) {
    this._currComponent = this._renderer.findComponentUnderMouse(e);

    const parent = Webgl2dRenderer.global.getRootParent(this._currComponent).slice(-1)[0];

    if (parent instanceof CropSelectionComponent && this._currComponent?.onMouseDown) {
      this._currComponent?.onMouseDown(e);
    } else {
      // We are not interacting with crop selection; allow hand panning.
      this._currComponent = null;
      this._renderer.tools.handTool.handleDown(e);
    }
  }

  private _handleMove(e: MouseEvent | TouchEvent) {
    if (this._currComponent) {
      this._currComponent.onMouseMove?.(e);
    } else {
      this._renderer.tools.handTool.handleMove(e);
    }
  }

  private _handleUp(e: MouseEvent | TouchEvent) {
    this._currComponent?.onMouseUp?.(e);
    this._currComponent = null;
  }

  private _handleClearTool() {
    this._renderer.cropSelectionComponent.transform.reset();
    this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
    this._dispose?.();
    this._dispose = null;
    this._currComponent = null;
  }

  private _initCropSelectionCanvasCentered(
    component: ImageRectComponent | ImageAdjustRectComponent,
  ) {
    try {
      // Ensure model matrix is available for global calculations.
      component.calcModelMatrix(undefined, true);

      const canvasSize = this._renderer.canvasSize.clone().round();
      if (canvasSize.hasZero()) return;

      this._renderer.cropSelectionComponent.transform.calcDimensions({
        position: this._renderer.canvasGlobalCenter,
        size: canvasSize,
        rotation: 0,
      });
      this._renderer.render();
    } catch {
      // noop
    }
  }

  private _setCursor(cursor: string) {
    if (cursor === this._lastCursor) return;
    this._lastCursor = cursor;
    this._renderer.canvas.style.cursor = cursor;
  }

  private _isMousePossiblyOverCropSelection(e: MouseEvent): boolean {
    try {
      const mouse = this._renderer.camera.getGlobalMousePosition(e);
      const crop = this._renderer.cropSelectionComponent;
      // Use component corners to build AABB in global coords (works even if rotated).
      const tl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.zero());
      const tr = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.oneZero());
      const br = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.one());
      const bl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.zeroOne());

      const minX = Math.min(tl.x, tr.x, br.x, bl.x);
      const maxX = Math.max(tl.x, tr.x, br.x, bl.x);
      const minY = Math.min(tl.y, tr.y, br.y, bl.y);
      const maxY = Math.max(tl.y, tr.y, br.y, bl.y);

      const scale = this._renderer.camera.scale || 1;
      const margin = CROP_CURSOR_AABB_MARGIN_PX / scale;

      return (
        mouse.x >= minX - margin &&
        mouse.x <= maxX + margin &&
        mouse.y >= minY - margin &&
        mouse.y <= maxY + margin
      );
    } catch {
      return true;
    }
  }

  private _getCursorByComponent(component: Nullable<Webgl2Component>): string {
    if (!component) return DEFAULT_CURSOR;

    if (
      component instanceof CropSelectionTopLeftComponent ||
      component instanceof CropSelectionBottomRightComponent
    ) {
      return "nwse-resize";
    }

    if (
      component instanceof CropSelectionTopRightComponent ||
      component instanceof CropSelectionBottomLeftComponent
    ) {
      return "nesw-resize";
    }

    if (
      component instanceof CropSelectionTopComponent ||
      component instanceof CropSelectionBottomComponent
    ) {
      return "ns-resize";
    }

    if (
      component instanceof CropSelectionLeftComponent ||
      component instanceof CropSelectionRightComponent
    ) {
      return "ew-resize";
    }

    if (component instanceof CropSelectionComponent) {
      return "move";
    }

    // Some tools (e.g. canvas resize) might be picked accidentally; keep default.
    if (component instanceof CanvasResizeSelectionComponent) {
      return DEFAULT_CURSOR;
    }

    return DEFAULT_CURSOR;
  }
}

export default CropTool;
